import { ProgressSpinnerComponent } from '@/shared/ui-components/progress-spinner/progress-spinner.component';
import { GlobalProgressSpinnerService } from '@/shared/services/global-progress-spinner.service';
import { GoogleAnalyticsService } from '@/core/analytics/google-analytics.service';
import { VersionCheckService } from '@/core/services/version-check.service';
import { LanguageService } from '@/shared/services/language.service';
import { ButtonComponent } from '@/shared/ui-components/button/button.component';
import { CommonModule } from '@angular/common';
import { Component, inject, OnInit } from '@angular/core';
import { RouterModule } from '@angular/router';
import { Toast } from 'primeng/toast';
import { SvgIconComponent } from 'angular-svg-icon';
import * as Sentry from "@sentry/angular";

@Component({
    selector: 'app-root',
    standalone: true,
    imports: [CommonModule, RouterModule, ProgressSpinnerComponent, Toast, SvgIconComponent, ButtonComponent],
    template: `
        <div class="app-root-shell">
            @if (_versionCheckService.isUpdateAvailable()) {
                <div
                    class="version-update-banner"
                    role="region"
                    aria-label="Application update available"
                    aria-live="polite"
                >
                    <div class="version-update-banner__inner">
                        <div class="version-update-banner__message">
                            <svg-icon
                                src="/icons/ai-sparkle.svg"
                                svgClass="version-update-banner__icon"
                                aria-hidden="true"
                            ></svg-icon>
                            <p class="version-update-banner__text">
                                <span class="version-update-banner__title">
                                    A new version of BettaAlpha is available.
                                </span>
                                <span class="version-update-banner__subtitle">
                                    Update now to get the latest features and improvements.
                                </span>
                            </p>
                        </div>
                        <div class="version-update-banner__actions">
                            <app-button
                                [label]="_versionCheckService.isRefreshing() ? 'Refreshing…' : 'Refresh Now'"
                                variant="primary"
                                size="small"
                                [rounded]="true"
                                startIcon="/icons/refresh.svg"
                                iconSvgClass="w-4 h-4 text-white"
                                [loading]="_versionCheckService.isRefreshing()"
                                [disabled]="_versionCheckService.isRefreshing()"
                                ariaLabel="Refresh the application to load the latest version"
                                styleClass="version-update-banner__refresh"
                                (onClick)="_versionCheckService.refreshNow()"
                            />
                            <button
                                type="button"
                                class="version-update-banner__dismiss"
                                aria-label="Dismiss update notification for this session"
                                [disabled]="_versionCheckService.isRefreshing()"
                                (click)="_versionCheckService.dismiss()"
                            >
                                <svg-icon src="/icons/cross.svg" svgClass="w-4 h-4" aria-hidden="true"></svg-icon>
                            </button>
                        </div>
                    </div>
                </div>
            }

            <div class="app-root-shell__main">
                <router-outlet></router-outlet>
            </div>
        </div>

        @if (_globalProgressSpinnerService.loading$ | async) {
            <app-progress-spinner />
        }

        <p-toast position="top-right" life="3000">
            <ng-template let-message #headless let-closeFn="closeFn">
                <div class="custom-toast" [ngClass]="message.severity">
                    <span class="custom-toast__icon" aria-hidden="true">
                        @switch (message.severity) {
                            @case ('error') {
                                <svg-icon src="/icons/exclamation.svg"></svg-icon>
                            }
                            @case ('warn') {
                                <svg-icon src="/icons/exclamation.svg"></svg-icon>
                            }
                            @case ('info') {
                                <svg-icon src="/icons/info.svg"></svg-icon>
                            }
                            @default {
                                <svg-icon src="/icons/check.svg"></svg-icon>
                            }
                        }
                    </span>
                    <div class="custom-toast__content text-paragraph-medium">{{ message.detail }}</div>
                    <button type="button" class="custom-toast__close" aria-label="Dismiss toast" (click)="closeFn($event)">
                        <svg-icon src="/icons/cross.svg" svgClass="w-4 h-4"></svg-icon>
                    </button>
                </div>
            </ng-template>
        </p-toast>
    `,
    styles: [`
        :host {
            display: block;
        }

        .app-root-shell {
            display: flex;
            flex-direction: column;
            min-height: 100vh;
        }

        .app-root-shell__main {
            flex: 1;
            min-height: 0;
        }

        .version-update-banner {
            flex-shrink: 0;
            position: relative;
            z-index: 40;
            color: var(--text-color, #033631);
            background: linear-gradient(
                90deg,
                rgba(198, 154, 114, 0.18) 0%,
                rgba(249, 250, 251, 0.72) 42%,
                rgba(3, 54, 49, 0.1) 100%
            );
            border-bottom: 1px solid rgba(3, 54, 49, 0.08);
            animation: version-banner-enter 0.35s ease-out;
        }

        .version-update-banner__inner {
            display: grid;
            grid-template-columns: 1fr auto 1fr;
            align-items: center;
            gap: 1rem;
            min-height: 5.25rem;
            padding: 1.25rem 1.5rem;
        }

        .version-update-banner__message {
            grid-column: 2;
            display: flex;
            align-items: center;
            justify-content: center;
            gap: 0.75rem;
            min-width: 0;
            text-align: center;
        }

        .version-update-banner__icon {
            width: 1.5rem;
            height: 1.5rem;
            flex-shrink: 0;
        }

        .version-update-banner__text {
            margin: 0;
            display: flex;
            flex-wrap: wrap;
            justify-content: center;
            align-items: baseline;
            gap: 0.25rem 0.4rem;
            color: var(--text-color, #033631);
            font-size: 1.0625rem;
            line-height: 1.45;
        }

        .version-update-banner__title {
            font-weight: 700;
            font-size: 1.125rem;
        }

        .version-update-banner__subtitle {
            font-weight: 400;
            font-size: 1.0625rem;
            color: rgba(3, 54, 49, 0.82);
        }

        .version-update-banner__actions {
            grid-column: 3;
            display: flex;
            align-items: center;
            justify-content: flex-end;
            gap: 0.5rem;
            flex-shrink: 0;
        }

        :host ::ng-deep .version-update-banner__refresh.p-button {
            transition: background-color 0.15s ease, box-shadow 0.15s ease, opacity 0.15s ease;
            padding-inline: 1rem;
        }

        :host ::ng-deep .version-update-banner__refresh.p-button:focus-visible {
            outline: 2px solid var(--text-color, #033631);
            outline-offset: 2px;
        }

        .version-update-banner__dismiss {
            display: inline-flex;
            align-items: center;
            justify-content: center;
            width: 2rem;
            height: 2rem;
            background: transparent;
            border: none;
            border-radius: 0.5rem;
            color: var(--text-color, #033631);
            cursor: pointer;
            transition: background-color 0.15s ease;
        }

        .version-update-banner__dismiss:hover:not(:disabled) {
            background: rgba(3, 54, 49, 0.08);
        }

        .version-update-banner__dismiss:focus-visible {
            outline: 2px solid var(--text-color, #033631);
            outline-offset: 2px;
        }

        .version-update-banner__dismiss:disabled {
            opacity: 0.5;
            cursor: not-allowed;
        }

        @keyframes version-banner-enter {
            from {
                transform: translateY(-0.75rem);
                opacity: 0;
            }
            to {
                transform: translateY(0);
                opacity: 1;
            }
        }

        @media (prefers-reduced-motion: reduce) {
            .version-update-banner {
                animation: none;
            }

            :host ::ng-deep .version-update-banner__refresh.p-button,
            .version-update-banner__dismiss {
                transition: none;
            }
        }

        @media (max-width: 768px) {
            .version-update-banner__inner {
                display: flex;
                flex-wrap: wrap;
                align-items: flex-start;
                min-height: 0;
                padding: 1rem;
            }

            .version-update-banner__message {
                flex: 1 1 100%;
                justify-content: flex-start;
                text-align: left;
            }

            .version-update-banner__actions {
                margin-left: auto;
            }

            .version-update-banner__title {
                font-size: 1rem;
            }

            .version-update-banner__subtitle {
                font-size: 0.9375rem;
            }
        }
    `]
})
export class AppComponent implements OnInit {
    public readonly _globalProgressSpinnerService = inject(GlobalProgressSpinnerService);
    public readonly _versionCheckService = inject(VersionCheckService);
    private readonly _googleAnalyticsService = inject(GoogleAnalyticsService);
    private readonly _languageService = inject(LanguageService);

    ngOnInit() {
        const isWindows = /Windows/i.test(navigator.userAgent);
        document.documentElement.style.setProperty('--base-font-size', isWindows ? '85%' : '100%');
        this._googleAnalyticsService.initialize();
        this._versionCheckService.start();
    }

    public throwTestError(): void {
    // Send a log before throwing the error
    Sentry.logger.info(Sentry.logger.fmt`User ${"sentry-test"} triggered test error button`, {
      action: "test_error_button_click",
    });
    // Send a test metric before throwing the error
    Sentry.metrics.count('test_counter', 1);
    throw new Error("Sentry Test Error");
  }
}
